import { CheckOutlineIcon, HelpPathOutlineIcon } from '@ikyucom/design-system-icons-react'
import { ElementButton, InputText, RadioButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { useCheckIsDiscountCouponEnabled } from '~/features/reservationInput/store/discountCoupon'
import { useIsDiscountCouponEnabled } from '~/features/reservationInput/store/stateMachine/context'
import { money } from '~/lib/money/money'
import { getHelpDiscountCouponUrl } from '~/lib/url'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { useIsRewardsVisible } from '../../store/payment'
import { checkedAcquiredCoupon, useAcquireCoupons } from './useAcquireCoupons'
import { useAppliedMessage } from './useAppliedMessage'
import { useDisabledDiscountCoupon } from './useDisabledAcquiredCoupon'
import { useInputCouponCode } from './useInputCouponCode'
import { useInputCouponCodeBody } from './useInputCouponCodeBody'

const styles = sva({
  slots: ['container', 'head', 'title', 'information', 'icon', 'content'],
  base: {
    container: {
      bg: 'white',
      paddingX: '3',
      paddingY: '4',
      spaceY: '3',
      _pc: {
        padding: '6',
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'space-between',
        spaceY: '0',
      },
    },
    head: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '2xl',
    },
    title: {
      fontWeight: 'bold',
    },
    information: {
      ml: '1',
    },
    icon: {
      color: 'gray.600',
    },
    content: {
      spaceY: '4',
      _pc: {
        w: '[424px]',
        border: 'medium',
        borderRadius: 'default',
        p: '4',
      },
    },
  },
})

export function DiscountCoupon() {
  const isVisible = useIsRewardsVisible()
  const classes = styles()
  if (!isVisible) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <h2 className={classes.title}>割引クーポン利用</h2>
        <a
          href={getHelpDiscountCouponUrl()}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="割引クーポン利用について詳細はこちら"
          className={classes.information}
        >
          <HelpPathOutlineIcon className={classes.icon} />
        </a>
      </div>
      <DiscountCouponBody />
    </div>
  )
}

export function DiscountCouponBody() {
  const { enabled, onChange } = useDiscountCouponBody()
  const classes = styles()
  return (
    <div className={classes.content}>
      <RadioButton name="couponEnabled" checked={!enabled} onChange={onChange(false)}>
        利用しない
      </RadioButton>
      <div className="space-y-2">
        <RadioButton name="couponEnabled" checked={enabled} onChange={onChange(true)}>
          利用する
        </RadioButton>
        <Input />
      </div>
    </div>
  )
}

function useDiscountCouponBody() {
  const enabled = useIsDiscountCouponEnabled()
  const change = useCheckIsDiscountCouponEnabled()
  const onChange = useCallback(
    (v: boolean) => {
      return () => change(v)
    },
    [change]
  )
  return { enabled, onChange }
}

function Input() {
  const enabled = useIsDiscountCouponEnabled()
  if (!enabled) {
    return null
  }
  return (
    <div className="ml-7 space-y-4 rounded bg-neutral-100 p-3">
      <AcquiredCoupons />
      <DisabledAcquiredCoupons />
      <InputCouponCode />
    </div>
  )
}

function AcquiredCoupons() {
  const { coupons, selectedCouponCode, couponType, changeCoupon } = useAcquireCoupons()
  return (
    <>
      {coupons.map((coupon) => (
        <div key={coupon.code}>
          <RadioButton
            name="coupon"
            value={coupon.code}
            checked={checkedAcquiredCoupon(coupon.code, couponType, selectedCouponCode)}
            onChange={changeCoupon}
          >
            {coupon.name}
          </RadioButton>
        </div>
      ))}
    </>
  )
}

function DisabledAcquiredCoupons() {
  const coupons = useDisabledDiscountCoupon()
  return (
    <>
      {coupons.map((coupon) => (
        <div key={coupon.code}>
          <RadioButton name="coupon" disabled={true}>
            {coupon.name}
          </RadioButton>
          <p className="mt-1 pl-7 text-sm font-bold text-neutral-700">
            利用条件：総額{money(coupon.minimumReservationAmount ?? 0)}円以上の予約
          </p>
        </div>
      ))}
    </>
  )
}

function InputCouponCode() {
  const { couponType, changeCoupon } = useInputCouponCode()
  return (
    <div className="space-y-2">
      <RadioButton
        name="coupon"
        value="InputtedCouponCode"
        checked={couponType?.type === 'InputtedCouponCode'}
        onChange={changeCoupon}
      >
        クーポンコード
      </RadioButton>
      <InputCouponCodeBody />
      <AppliedMessage />
    </div>
  )
}

function AppliedMessage() {
  const { appliedCoupon, applied } = useAppliedMessage()
  if (!applied) {
    return <></>
  }
  return (
    <div className="flex items-center">
      <CheckOutlineIcon className={css({ mr: '1', color: 'success' })} />
      <p>
        ご予約金額に
        <span className={css({ fontWeight: 'bold', color: 'tertiary' })}>
          {money(appliedCoupon?.discountAmount ?? 0)}
          円割引
        </span>
        が適用されました
      </p>
    </div>
  )
}

function InputCouponCodeBody() {
  const { couponType, inputCouponCode, submit } = useInputCouponCodeBody()
  return (
    <div className={hstack({ alignItems: 'start', gap: '3' })}>
      <div className={css({ w: 'full', flex: '1' })}>
        <InputText
          placeholder="0000000000000000"
          withSpread={true}
          onChange={inputCouponCode}
          errorMessage={
            couponType?.type === 'InputtedCouponCode' ? couponType.errorMessage : undefined
          }
        />
      </div>
      <ElementButton
        label="適用"
        color="tertiary"
        buttonClassName="py-3"
        disabled={couponType?.type !== 'InputtedCouponCode'}
        onClick={submit}
      />
    </div>
  )
}
